import { Injectable } from "@angular/core";

import MD5 from "md5-es";
import { combineLatest, Observable, of, throwError } from "rxjs";
import { catchError, first, map, switchMap } from "rxjs/operators";

import { AppserverBrugerCredentialsRepository, AppserverCredentials, AppserverUnauthenticatedCredentials, opretTomAppserverCredentials } from "../credentials";
import { ForbrugerOplysningerRepository } from "../forbruger-oplysninger";
import { BrugerRepository } from "../bruger/bruger.repository";
import { ForbrugerRepository } from "../forbruger/forbruger.repository";
import { ForsyningIndstillinger, ForsyningIndstillingerService } from "../forsyning-indstillinger";
import { AppserverHttpClient, BrugerHttpClient, EksternForsyningHttpClient, InstallationHttpClient, ParametreForAktuelleInstallation } from "../http";
import { UmbracoIndstillingerObserver } from "../umbraco-indstillinger";
import { ForbrugerTilknytning } from "./model/forbruger-tilknytning.model";

@Injectable({ providedIn: "root" })
export class ForbrugerTilknytningRepository {
    constructor(
        private readonly brugerHttpClient: BrugerHttpClient,
        private readonly installationHttpClient: InstallationHttpClient,
        private readonly eksternHttpClient: EksternForsyningHttpClient,
        private readonly forbrugerOplysningerRepository: ForbrugerOplysningerRepository,
        private readonly brugerCredentialsRepository: AppserverBrugerCredentialsRepository,
        private readonly forsyningIndstillingerService: ForsyningIndstillingerService,
        private readonly umbracoIndstillingerObserver: UmbracoIndstillingerObserver,
        private readonly brugerRepository: BrugerRepository,
        private readonly forbrugerRepository: ForbrugerRepository
    ) {}

    public async opretTilknytning(forsyningId: string, brugernavn: string, kodeord: string): Promise<void> {
        const appServerHttpClient = await this.eksternHttpClient.authenticate(brugernavn, MD5.hash(kodeord), forsyningId).pipe(first()).toPromise();

        const forbrugerHvisInstallationSkalAdministreres = await this.forbrugerRepository.hentForbruger(appServerHttpClient).pipe(first()).toPromise();

        const aktuelBruger = await this.brugerRepository.hentAktuelleBruger().pipe(first()).toPromise();

        return this.erInternForsyning(forsyningId)
            ? this.opretInternTilknytning(aktuelBruger.id, brugernavn, kodeord)
            : this.opretEksternTilknytning(aktuelBruger.id, forbrugerHvisInstallationSkalAdministreres.mailAdresse, forsyningId, brugernavn, kodeord);
    }

    public async sletTilknytning(tilknytningId: any): Promise<void> {
        const url = "api/SletTilknytning";
        // eslint-disable-next-line @typescript-eslint/naming-convention
        const params = { RelationId: tilknytningId };
        await this.installationHttpClient.postWithStringResult(url, params).pipe(first()).toPromise();
    }

    /** Gentag ekstern authenticate med gemt hash (fx efter midlertidig fejl). */
    public async retryEksternAuthenticate(noegle: AppserverUnauthenticatedCredentials): Promise<boolean> {
        if (!noegle?.brugerNavn || !noegle?.kodeordKrypteret || !noegle?.forsyningId) {
            return false;
        }
        try {
            const api = await this.eksternHttpClient.authenticate(noegle.brugerNavn, noegle.kodeordKrypteret, noegle.forsyningId).pipe(first()).toPromise();
            return !!api?.credentials?.cryptId;
        } catch {
            return false;
        }
    }

    public async validerEksternMedPlaintextKodeord(forsyningId: string, brugernavn: string, kodeord: string): Promise<boolean> {
        return this.retryEksternAuthenticate({
            forsyningId,
            brugerNavn: brugernavn,
            kodeordKrypteret: MD5.hash(kodeord)
        });
    }

    public hentTilknytninger(brugerId: number, httpClient: AppserverHttpClient): Observable<ForbrugerTilknytning[]> {
        if (brugerId === null) {
            return of([]);
        }
        const forsyningId$ = this.umbracoIndstillingerObserver.valueChanges.pipe(map((i) => i.aktuelEforsyning && i.aktuelEforsyning.forsyningId));
        return forsyningId$.pipe(
            switchMap((forsyningId) => {
                const url = "api/henttilknytninger";
                // eslint-disable-next-line @typescript-eslint/naming-convention
                const params = { EBrugerId: brugerId };
                return httpClient.post(url, params, ParametreForAktuelleInstallation.false).pipe(
                    map((d) => this.dtoToTilknytninger(d, forsyningId)),
                    switchMap((d) => this.tilfoejAuthenticationData(d)),
                    switchMap((d) => this.tilfoejForsyningIndstillinger(d)),
                    switchMap((d) => this.tilfoejForbruger(d))
                );
            })
        );
    }

    private async opretInternTilknytning(ebrugerId: number, brugernavn: string, kodeord: string): Promise<void> {
        const url = "api/tilknytejendom";
        const params = {
            eBrugerId: ebrugerId,
            email: this.isNumber(brugernavn) ? "" : brugernavn,
            tilknytEjdNr: this.isNumber(brugernavn) ? brugernavn : -1,
            pwd: MD5.hash(kodeord)
        };
        await this.installationHttpClient.postWithStringResult(url, params).pipe(first()).toPromise();
    }

    private async opretEksternTilknytning(ebrugerId: number, notificerEmail: string, forsyningId: string, brugernavn: string, kodeord: string): Promise<void> {
        const url = "api/TilfoejEksternTilknytning";
        const params = {
            /* eslint-disable @typescript-eslint/naming-convention */
            EBrugerId: ebrugerId,
            ForsyningId: forsyningId,
            EBrugerNavn: brugernavn,
            EBrugerKodeordKrypteret: MD5.hash(kodeord),
            NotificerEmail: notificerEmail
            /* eslint-enable @typescript-eslint/naming-convention */
        };
        await this.installationHttpClient.postWithStringResult(url, params).pipe(first()).toPromise();
    }

    private erInternForsyning(forsyningId: string): boolean {
        return !forsyningId || forsyningId.toUpperCase() === (this.installationHttpClient.credentials.forsyningId || "").toUpperCase();
    }

    private isNumber(value: any): boolean {
        return new RegExp("^[0-9]+$").test(value);
    }

    private dtoToTilknytninger(data: any, forsyningId: string): ForbrugerTilknytning<AppserverUnauthenticatedCredentials>[] {
        const interne = data.InterneTilknytninger.map((t) => this.dtoToTilknytning(t, forsyningId, true));
        const eksterne = data.EksterneTilknytninger.map((t) => this.dtoToTilknytning(t, forsyningId, false));
        return [...interne, ...eksterne];
    }

    private dtoToTilknytning(data: any, forsyningId: string, erInternTilknytning: boolean): ForbrugerTilknytning<AppserverUnauthenticatedCredentials> {
        const brugerForsyningId = this.brugerCredentialsRepository.hent().forsyningId;
        const erAktuelleSite = !data.ForsyningId || data.ForsyningId === brugerForsyningId;
        const resolvedForsyningId = data.ForsyningId || forsyningId;
        const eksternTilknytningsNoegle: AppserverUnauthenticatedCredentials | undefined =
            !erInternTilknytning && data.EBrugerNavn && data.EBrugerKodeordKrypteret && resolvedForsyningId
                ? {
                      forsyningId: resolvedForsyningId,
                      brugerNavn: String(data.EBrugerNavn),
                      kodeordKrypteret: String(data.EBrugerKodeordKrypteret)
                  }
                : undefined;
        return {
            tilknytningId: data.RelationId,
            ejendomNr: data.EjendomNr,
            forbrugerNavn: data.Navn,
            forbrugerAdresse: data.Adresse,
            tilknytningPostNr: data.PostNr != null && data.PostNr !== "" ? String(data.PostNr) : undefined,
            tilknytningBy: data.By != null && data.By !== "" ? String(data.By) : undefined,
            tilknytningMaalerNr:
                data["MålerNr"] != null && data["MålerNr"] !== ""
                    ? String(data["MålerNr"])
                    : data.MaalerNr != null && data.MaalerNr !== ""
                      ? String(data.MaalerNr)
                      : undefined,
            eksternEBrugerNavn: data.EBrugerNavn != null && data.EBrugerNavn !== "" ? String(data.EBrugerNavn) : undefined,
            erAktuelleSite,
            erInternTilknytning,
            erEksternTilknytning: !erInternTilknytning,
            eksternTilknytningsNoegle,
            credentials: {
                forsyningId: resolvedForsyningId,
                brugerNavn: data.EBrugerNavn,
                kodeordKrypteret: data.EBrugerKodeordKrypteret
            },
            forsyningIndstillinger: {
                forsyningId: resolvedForsyningId,
                forsyningNodeId: null,
                appServerUrl: null,
                eforsyningUrl: null,
                forsyningNavn: null,
                sideId: null,
                matomoSiteId: "",
                pilotKundeToggle: data.PilotKundeToggle || false
            }
        };
    }

    private tilfoejAuthenticationData(tilknytninger: ForbrugerTilknytning<AppserverUnauthenticatedCredentials>[]): Observable<ForbrugerTilknytning[]> {
        if (!tilknytninger.length) {
            return of([]);
        }

        return combineLatest(
            tilknytninger.map((t) =>
                this.hentCredentials(t.credentials).pipe(
                    map((credentials) => {
                        const erIkkeTilgaengelig = t.erEksternTilknytning && !credentials.cryptId;

                        const loginSomTekst = (t.eksternEBrugerNavn || t.eksternTilknytningsNoegle?.brugerNavn || "").trim();
                        const ejendomSomTekst =
                            t.ejendomNr != null && String(t.ejendomNr).trim() !== "" ? String(t.ejendomNr).trim() : "";
                        const fallbackNavn = loginSomTekst || (ejendomSomTekst ? `Forbruger ${ejendomSomTekst}` : "Ekstern tilknytning");
                        const postNrBy = [t.tilknytningPostNr, t.tilknytningBy]
                            .filter((x) => x != null && String(x).trim() !== "")
                            .map((x) => String(x).trim())
                            .join(" ")
                            .trim();

                        return {
                            ...t,
                            credentials,
                            erIkkeTilgaengelig,
                            fejlbesked: erIkkeTilgaengelig
                                ? "Kunne ikke logge ind på tilknyttet forbruger."
                                : t.fejlbesked,
                            forbrugerNavn: erIkkeTilgaengelig && !(t.forbrugerNavn || "").trim() ? fallbackNavn : t.forbrugerNavn,
                            forbrugerAdresse:
                                erIkkeTilgaengelig && !(t.forbrugerAdresse || "").trim() && postNrBy
                                    ? postNrBy
                                    : t.forbrugerAdresse
                        };
                    }),
                    catchError(() =>
                        of({
                            ...t,
                            credentials: opretTomAppserverCredentials(),
                            erIkkeTilgaengelig: t.erEksternTilknytning,
                            fejlbesked: "Kunne ikke logge ind på tilknyttet forbruger."
                        })
                    )
                )
            )
        );
    }

    private hentCredentials(auth: AppserverUnauthenticatedCredentials): Observable<AppserverCredentials> {
        if (auth.brugerNavn && auth.kodeordKrypteret && auth.forsyningId) {
            return this.eksternHttpClient.authenticate(auth.brugerNavn, auth.kodeordKrypteret, auth.forsyningId).pipe(
                map((api) => api.credentials),
                catchError(() =>
                    // TODO: Log info/warning om at vi ikke kan authenticate. Der er måske ikke adgang til serveren.
                    of(opretTomAppserverCredentials())
                )
            );
        }

        const brugerCredentials = this.brugerHttpClient.credentials;
        if (auth.forsyningId === brugerCredentials.forsyningId) {
            return of(brugerCredentials);
        }

        const installationCredentials = this.installationHttpClient.credentials;
        if (auth.forsyningId === installationCredentials.forsyningId) {
            return of(installationCredentials);
        }

        return throwError("Kan ikke oprette authentication data");
    }

    private tilfoejForsyningIndstillinger(tilknytninger: ForbrugerTilknytning[]): Observable<ForbrugerTilknytning[]> {
        if (!tilknytninger.length) {
            return of(new Array<ForbrugerTilknytning>());
        }

        return combineLatest(
            tilknytninger.map((t) =>
                this.hentForsyningIndstillinger(t.forsyningIndstillinger.forsyningId).pipe(map((forsyningIndstillinger) => ({ ...t, forsyningIndstillinger })))
            )
        );
    }

    private hentForsyningIndstillinger(forsyningId: string): Observable<ForsyningIndstillinger> {
        return this.forsyningIndstillingerService.hentForsyningIndstillinger(forsyningId);
    }

    private tilfoejForbruger(tilknytninger: ForbrugerTilknytning[]): Observable<ForbrugerTilknytning[]> {
        if (!tilknytninger.length) {
            return of(new Array<ForbrugerTilknytning>());
        }

        return combineLatest(tilknytninger.map((tilknytning) => this.hentForbruger(tilknytning).pipe(map((forbruger) => ({ ...tilknytning, ...forbruger })))));
    }

    private hentForbruger(tilknytning: ForbrugerTilknytning): Observable<Partial<ForbrugerTilknytning>> {
        if (tilknytning.forbrugerNavn) {
            return of(tilknytning);
        }

        // Vi har ikke kunnet authenticate. Så vi har ikke noget sted at hente fra.
        if (!tilknytning.credentials.cryptId) {
            return of({
                ...tilknytning,
                erIkkeTilgaengelig: tilknytning.erEksternTilknytning || tilknytning.erIkkeTilgaengelig,
                fejlbesked: tilknytning.fejlbesked || "Kunne ikke logge ind på tilknyttet forbruger."
            });
        }

        return this.eksternHttpClient.authenticate(tilknytning.credentials).pipe(
            switchMap((api) => this.forbrugerOplysningerRepository.hentForbrugerOplysninger(api)),
            map((forbruger) => ({
                ejendomNr: forbruger.ejendomNr,
                forbrugerNavn: forbruger.navn,
                forbrugerAdresse: forbruger.adresse
            })),
            catchError(() =>
                // TODO: Log info/warning om at vi ikke kan hente data. Der er måske ikke adgang til serveren.
                of({
                    ejendomNr: tilknytning.ejendomNr || 0,
                    forbrugerNavn: tilknytning.forbrugerNavn || "",
                    forbrugerAdresse: tilknytning.forbrugerAdresse || "",
                    erIkkeTilgaengelig: true,
                    fejlbesked: "Kunne ikke hente oplysninger for tilknyttet forbruger."
                })
            )
        );
    }
}
